<template>
    <v-row class="info-input ma-0">
        <v-col cols="12" md="6" class="info-input__column">
            <section class="info-input__section">
                <h3>{{ infoType }} Contact Information</h3>
                <div class="info-input__field-row">
                    <v-text-field
                        v-model="contact.firstName"
                        outlined
                        hide-details
                        dense
                        v-bind="$attrs"
                        label="First Name"
                    />
                    <v-text-field
                        v-model="contact.lastName"
                        outlined
                        hide-details
                        dense
                        v-bind="$attrs"
                        label="Last Name"
                    />
                </div>
                <v-text-field v-model="contact.company" hide-details outlined dense v-bind="$attrs" label="Company" />
                <v-text-field v-model="contact.email" hide-details v-bind="$attrs" label="Email" outlined dense />
                <base-tel-input
                    id="phone"
                    ref="phone"
                    v-model="phoneInput"
                    hide-details
                    class="tele-input phone-input"
                    valid-characters-only
                    :dropdown-options="{
                        showDialCodeInSelection: true,
                        showFlags: true,
                        showDialCodeInList: true,
                        tabindex: 0
                    }"
                    :default-country="$auth.user.phoneCountryCode"
                    :input-options="{
                        placeholder: 'Phone',
                        maxlength: 25,
                        autocomplete: 'on',
                        name: 'telephone',
                        required: false,
                        autofocus: false,
                        id: '',
                        readonly: false,
                        tabindex: 0,
                        type: 'tel',
                        styleClasses: ''
                    }"
                    @update="assignPhone"
                />
            </section>
        </v-col>
        <v-col cols="12" md="6" class="info-input__column">
            <section class="info-input__section">
                <h3>{{ infoType }} Address Information</h3>
                <v-text-field
                    ref="address-line1"
                    v-model="address.line1"
                    outlined
                    dense
                    hide-details
                    v-bind="$attrs"
                    label="Address Line 1"
                />
                <v-text-field
                    ref="address-line2"
                    v-model="address.line2"
                    outlined
                    dense
                    hide-details
                    v-bind="$attrs"
                    label="Address Line 2"
                />
                <div class="info-input__field-row">
                    <v-autocomplete
                        ref="country"
                        v-model="address.country"
                        outlined
                        dense
                        hide-details
                        v-bind="$attrs"
                        :items="countries"
                        item-text="name"
                        item-value="countryCode"
                        label="Country"
                        placeholder="Country"
                        @change="countryChanged"
                    />
                    <v-autocomplete
                        ref="state"
                        v-model="address.state"
                        outlined
                        dense
                        hide-details
                        v-bind="$attrs"
                        item-text="name"
                        item-value="stateCode"
                        :loading="statesLoading"
                        :disabled="statesLoading || !address.country"
                        :items="stateProvinces"
                        label="State/Province/Region"
                        placeholder="State"
                    />
                </div>
                <div class="info-input__field-row">
                    <v-text-field
                        ref="city"
                        v-model="address.city"
                        outlined
                        dense
                        hide-details
                        v-bind="$attrs"
                        label="City"
                    />
                    <v-text-field
                        ref="zip"
                        v-model="address.postalCode"
                        outlined
                        dense
                        hide-details
                        v-bind="$attrs"
                        label="ZIP / Postal Code"
                    />
                </div>
            </section>
        </v-col>
    </v-row>
</template>

<script>
import formRules from '@/utils/form_rules'

export default {
    name: 'InfoInput',
    props: {
        value: {
            type: Object,
            required: false,
            default: () => {}
        },
        infoType: {
            type: String,
            required: false,
            default: ``
        }
    },
    data() {
        return {
            statesLoading: false,
            citiesLoading: false,
            formRules,
            phoneInput: ``,
            contact: {
                company: ``,
                firstName: ``,
                lastName: ``,
                email: ``,
                phone: ``,
                phoneCountryCode: ``
            },
            address: {
                line1: ``,
                line2: ``,
                city: ``,
                state: ``,
                postalCode: ``
            }
        }
    },
    computed: {
        oidcBilling() {
            return this.$auth.user.address ? this.$auth.user.address.billing : {}
        },
        oidcShipping() {
            return this.$auth.user.address ? this.$auth.user.address.shipping : {}
        },
        countries() {
            return this.$store.getters[`location/getCachedCountries`]
        },

        cities() {
            return this.$store.getters[`location/getCachedCities`]({
                countryCode: this.address.country,
                stateCode: this.address.state
            })
        },
        stateProvinces() {
            return this.$store.getters[`location/getCachedStates`]({
                countryCode: this.address.country
            })
        }
    },
    watch: {
        'address.country': {
            immediate: true,
            async handler(v) {
                if (v) {
                    this.statesLoading = true
                    await this.$store.dispatch(`location/cacheStates`, {
                        countryCode: v
                    })
                    this.statesLoading = false
                }
            }
        },
        value: {
            immediate: true,
            deep: true,
            handler(v) {
                if (v) {
                    this.contact = v.contact
                    this.address = v.address
                }
            }
        }
    },
    async mounted() {
        await this.$store.dispatch(`location/cacheStates`, {
            countryCode: this.address.country
        })
    },
    methods: {
        async countryChanged(v) {
            if (v) {
                this.statesLoading = true
                try {
                    await this.$store.dispatch(`location/cacheStates`, {
                        countryCode: v
                    })
                    this.statesLoading = false
                } catch {
                    this.statesLoading = false
                }
            } else this.address.state = ``
        },

        async getCities(event) {
            this.citiesLoading = true
            try {
                const [countryCode, stateCode] = event
                await this.$store.dispatch(`location/cacheCities`, {
                    countryCode,
                    stateCode
                })
            } catch {
                this.citiesLoading = false
            } finally {
                this.citiesLoading = false
            }
        },
        assignPhone(a) {
            // eslint-disable-next-line no-unused-vars
            const [input, phoneObject] = a
            this.phoneValid = phoneObject.nationalNumber ? !!phoneObject.valid : true
            this.contact.phone = phoneObject.nationalNumber
            this.contact.phoneCountryCode = phoneObject.countryCode
        }
    }
}
</script>

<style scoped>
.info-input {
    color: #f2f4fa;
    font-family: 'Inter Tight', 'Roboto', sans-serif;
}
.info-input__column {
    min-width: 0;
    padding: 4px;
}
.info-input__section {
    display: flex;
    flex-direction: column;
    gap: 12px;
    height: 100%;
    padding: 16px;
    background: #22252d;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 12px;
    text-align: left;
}
.info-input__section h3 {
    margin: 0 0 4px;
    padding-bottom: 12px;
    border-bottom: 1px solid rgba(240, 120, 76, 0.35);
    font-size: 13px;
    font-weight: 600;
    line-height: 1.5;
}
.info-input__field-row {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px;
    min-width: 0;
}
.info-input__field-row > * {
    min-width: 0;
    margin: 0;
}
.info-input ::v-deep .v-input,
.info-input ::v-deep .v-label,
.info-input ::v-deep input {
    font-family: 'Inter Tight', 'Roboto', sans-serif;
    font-size: 12px;
}
.info-input ::v-deep .v-text-field--outlined {
    border-radius: 7px;
}
.info-input ::v-deep .v-text-field--outlined > .v-input__control > .v-input__slot {
    background: #282b33;
}
.info-input ::v-deep .v-text-field--outlined fieldset {
    border-color: rgba(255, 255, 255, 0.24);
}
.info-input ::v-deep .v-label {
    color: #b9bfcc;
}
.info-input ::v-deep .v-input input {
    color: #f2f4fa;
}
.info-input ::v-deep .v-input--is-focused fieldset {
    border-color: #f0784c !important;
}
.info-input ::v-deep .v-input--is-focused .v-label {
    color: #f0784c !important;
}
.info-input ::v-deep .v-input--is-disabled {
    opacity: 0.65;
}
.info-input ::v-deep .v-input__icon .v-icon {
    color: #b9bfcc;
}
.info-input ::v-deep .main-phone-div {
    height: auto;
    min-height: 40px;
}
.info-input ::v-deep .phone-border,
.info-input ::v-deep .phone-border-error {
    background: #282b33;
    border-radius: 7px;
}
.info-input ::v-deep .phone-border {
    border-color: rgba(255, 255, 255, 0.24);
}
.info-input ::v-deep .phone-border:focus-within {
    border-color: #f0784c;
    box-shadow: 0 0 0 1px #f0784c inset;
}
.info-input ::v-deep .vti__input {
    min-width: 0;
    color: #f2f4fa;
    background: transparent;
    border-radius: 0 7px 7px 0;
}
.info-input ::v-deep .vti__input::placeholder {
    color: #b9bfcc;
    opacity: 1;
}
.info-input ::v-deep .vti__dropdown {
    color: #f2f4fa;
    border-radius: 7px 0 0 7px;
}
.info-input ::v-deep .vti__dropdown:hover,
.info-input ::v-deep .vti__dropdown.open,
.info-input ::v-deep .vti__dropdown-list {
    color: #f2f4fa;
    background: #282b33;
}
.info-input ::v-deep .vti__dropdown-list {
    max-width: min(300px, calc(100vw - 80px));
    border-color: rgba(255, 255, 255, 0.24);
    font-size: 12px;
}
.info-input ::v-deep .vti__dropdown-item.highlighted {
    background: #443029;
}
.info-input ::v-deep .vti__dropdown:focus-visible {
    outline: 2px solid #f0784c;
    outline-offset: -2px;
}
@media (max-width: 599px) {
    .info-input__field-row {
        grid-template-columns: 1fr;
        gap: 12px;
    }
    .info-input__section {
        padding: 14px 12px;
    }
}
</style>
